<template>
    <v-card rounded class="crud-table card-shadow px-0 py-2 py-lg-4">
        <v-data-table :loading="loading" :headers="headers" :items="items" mobile-breakpoint="0" sort-by="calories" :hide-default-footer="serverPagination || noPagination" :server-items-length="serverPagination ? perPage : undefined" class="e-data-table elevation-0">
            <template v-slot:top>
                <div class="header px-2 px-lg-3 mb-4">
                    <v-toolbar height="auto" flat class="pt-2 pt-lg-0">
                        <v-toolbar-title class="d-flex align-center font-weight-medium">
                            <span style="font-size: 1.15rem">
                                <v-icon v-if="titleIcon" color="strong-text" size="25" class="me-1">{{titleIcon}}</v-icon>
                                {{title}}
                            </span>
                            <div v-if="pcOnly && total" class="ms-2">
                                <v-chip small outlined label class="px-2" style="height: 20px">
                                    <span class="primary--text font-weight-bold">{{numFormat(total)}}</span>
                                </v-chip>
                            </div>
                        </v-toolbar-title>
                        <!-- <v-divider class="mx-4" inset vertical></v-divider> -->
                        <v-spacer></v-spacer>
                        <div class="d-flex align-center">
                            <form v-if="showSearch && pcOnly" @submit.prevent="$emit('search', searchQuery)">
                                <v-text-field width="200" type="search" :append-icon="searchQuery ? 'mdi-close' : 'mdi-magnify'" @click:append="resetSearch" hide-details outlined label="البحث" :placeholder="searchPlaceholder" dense v-model="searchQuery"/>
                            </form>
                            <v-btn class="ms-6" v-if="!hideCreate" color="primary" dark @click="$emit('create')" depressed>
                                <v-icon left size="20" class="me-1">mdi-plus-circle-outline</v-icon>
                                {{createButtonText}}
                            </v-btn>

                            <slot name="header-actions" />
                        </div>
                    </v-toolbar>

                    <form v-if="showSearch && phoneOnly" @submit.prevent="$emit('search', searchQuery)" class="px-4 my-4">
                        <v-text-field width="200" type="search" :append-icon="searchQuery ? 'mdi-close' : 'mdi-magnify'" @click:append="resetSearch" hide-details outlined label="البحث" :placeholder="searchPlaceholder" dense v-model="searchQuery"/>
                    </form>
                </div>
            </template>
            <template v-slot:item.status="{ item }">
                <v-chip label small :color="handleStatus(item.status).color" :dark="!!handleStatus(item.status).color">
                    {{handleStatus(item.status).text}}
                </v-chip>
            </template>
            <template v-for="header in headers.filter(h=>(h.chip || h.time || h.price || h.boolean || h.ltr || h.flag || h.money || h.number || h.image || h.avatar || h.button || h.externalLink))" v-slot:[`item.${header.value}`]="{ item }">
                <div :key="'slot-' + header.name">

                    <v-chip v-if="header.chip" :outlined="!header.chipFunction(item[header.value]).filled" label small :color="header.chipFunction(item[header.value]).color" :dark="!!header.chipFunction(item[header.value]).color">
                        <v-icon size="18" class="me-2" style="margin-right: -3px">{{header.chipFunction(item[header.value]).icon}}</v-icon>
                        <strong>{{header.chipFunction(item[header.value]).text}}</strong>
                    </v-chip>
                    <div v-if="header.price || header.money" :class="{'primary--text font-weight-bold': !!item[header.value] && !header.color, [`${header.color}--text font-weight-bold`]: !!header.color}">
                        {{item[header.value] ? moneyFormat(item[header.value]) : moneyFormat(0)}}
                    </div>
                    <div v-if="header.number" class="font-weight-bold" :class="{'primary--text': !!item[header.value] && !header.color, [`${header.color}--text`]: !!header.color}">
                        {{item[header.value] ? numFormat(item[header.value]) : 0}}
                    </div>
                    <div v-else-if="header.image" class="py-3">
                        <v-img centered style="border-radius: 5px" cover width="80" height="60" :src="item[header.value]" :to="header.href ? item[header.href] : ''" v-ripple="header.href ? {class: 'white--text'} : false" :class="{'e-link': header.href}" />
                    </div>
                    <div v-else-if="header.avatar" class="py-3">
                        <v-avatar centered cover size="44" :to="header.href ? item[header.href] : ''" v-ripple="header.href ? true : false" :class="{'e-link': header.href}" >
                            <v-img :src="item[header.value]" ></v-img>
                        </v-avatar>
                    </div>
                    <div v-else-if="header.flag" class="py-3">
                        <country-flag v-if="item[header.value]" :country="item[header.value]" size="normal" />
                        <span style="font-size: 1.2rem" v-else>🏳️</span>
                    </div>
                    <component v-else-if="header.button" :is="item[header.button] ? 'v-btn' : 'div'" :key="'slot-' + header.value" :color="header.buttonColor || header.color || 'strong-text'" :to="item[header.button]" style="text-transform: capitalize;" class="medium-text px-3 py-4" :class="{'font-weight-bold': header.bold}" small :text="!header.filled" depressed>
                        {{item[header.value]}}
                    </component>
                    <v-btn as="a" v-else-if="header.externalLink" :key="'slot-' + header.value" :color="header.color|| 'primary'" :href="item[header.externalLink]" target="_blank" style="text-transform: capitalize;" class="medium-text px-3 py-4" dark :class="{'font-weight-bold': header.bold}" small depressed >
                        <v-icon left>mdi-open-in-new</v-icon>
                        {{item[header.value]}}
                    </v-btn>
                    <div v-else-if="header.ltr" style="direction: ltr; text-align: right;">
                        {{item[header.value]}}
                    </div>
                    <div v-else-if="header.boolean">
                        <v-chip v-if="!!item[header.value]" color="success" dark small class="px-3">نعم</v-chip>
                        <v-chip v-else color="muted" dark small class="px-3">ﻻ</v-chip>
                    </div>
                    <div v-else-if="header.time">
                        <v-tooltip v-if="item[header.value]" transition="fade-transition" color="tooltip" bottom>
                            <template v-slot:activator="{on, attrs}">
                                <div v-on="on" v-bind="attrs" >
                                    <v-chip small outlined label>
                                        <span class="details-text">{{timeFormat(item[header.value])}}</span>
                                    </v-chip>
                                </div>
                            </template>
                            <div class="ltr-dir">
                                {{dateTimeFormat(item[header.value])}}
                            </div>
                        </v-tooltip>
                    </div>
                </div>
            </template>
            <template v-slot:item.actions="{ item }">
                <div class="d-flex align-center">
                    <div v-if="sortIcon" class="me-3">
                        <v-icon color="muted" size="20" style="opacity: 0.5">mdi-cursor-move</v-icon>
                    </div>
                    <!-- <v-btn class="me-1" v-for="action in actions" :key="action.name" v-show="!action.show || item[action.show]" icon :color="action.color" :disabled="action.disabled && item[action.disabled]" @click="fireAction(action.name, item)">
                        <v-icon size="22">{{action.icon}}</v-icon>
                    </v-btn> -->
                    <!-- dots menu instead of buttons -->
                    <v-menu bottom left :offset-y="true" transition="slide-y-transition">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn color="muted" icon v-bind="attrs" v-on="on" :disabled="!actions || actions.length === 0">
                                <v-icon>mdi-dots-vertical</v-icon>
                            </v-btn>
                        </template>
                        <v-list dense min-width="150" >
                            <v-list-item dense v-for="(action) in actions" :key="action.name" v-show="!action.show || item[action.show]" :disabled="action.disabled && item[action.disabled]" @click="fireAction(action.name, item)">
                                <v-list-item-icon class="me-1">
                                    <v-icon :color="action.color" size="19">{{action.icon}}</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title class="medium-text">{{ action.text }}</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>


            </template>
            <template v-slot:no-data>
                <div class="my-5">
                    <div class="mb-3">لم يتم العثور على بيانات...</div>
                    <v-btn outlined text color="primary" @click="resetSearch">
                        <v-icon left>mdi-arrow-right</v-icon>
                        عودة للخلف
                    </v-btn>
                </div>
            </template>
        </v-data-table>

        <div class="mt-4" v-if="serverPagination && pageCount > 1">
            <v-pagination color="primary" :total-visible="pcOnly ? 10 : 4" v-model="page" :length="pageCount" ></v-pagination>
        </div>
    </v-card>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'

export default {
    props:{
        title: String,
        titleIcon: {},
        headers: {},
        items: {},
        hideCreate: {type: Boolean, default: false},
        sortIcon: {type: Boolean, default: false},
        showSearch: {type: Boolean, default: false},
        createButtonText: {type: String, default: 'إضافة جديد'},
        searchPlaceholder: {type: String, default: 'البحث في الجدول...'},
        actions: Array,

        serverPagination: {type: Boolean, default: false},
        noPagination: {type: Boolean, default: false},
        pageCount: {type: Number, default: 1}, // how many pages (last_page)
        currentPage: {type: Number, default: 1},
        perPage: {type: Number, default: 20},
        total: {type: Number},
        loading: {type: Boolean, default: false},
    },
    data() {
        return {
            page: 0,
            searchQuery: '',
        }
    },
    computed: {
        ...GlobalComputed,
    },
    watch: {
        page(page, old){
            if (old > 0){
                const query = this.searchQuery;
                this.$emit('load', {page, query});
            }
        },
        dialog (val) {
            val || this.close()
        },
        dialogDelete (val) {
            val || this.closeDelete()
        },
        currentPage(page){
            this.page = page;
        }
    },
    methods: {
        ...GlobalMethods,
        fireAction(action, item) {
            this.$emit(action, item);
        },
        resetSearch(){
            this.searchQuery = '';
            this.$emit('reset');
        }
    },
    mounted(){
        this.page = this.currentPage;

        const queryParam = new URLSearchParams(window.location.search).get('query');
        if (queryParam){
            this.searchQuery = queryParam;
            this.$emit('search', this.searchQuery);
        }
    }
}

</script>

<style lang="scss" scoped>
    section {
        .headline {
            font-size: 2rem !important;
            line-height: 3rem !important;
        }

        .description {
            line-height: 2rem !important;
        }
    }

    @media(max-width: 960px) {
        section {

            .headline {
                font-size: 1.65rem !important;
                line-height: 2.7rem !important;
            }

            .actions,
            .headline,
            .description {
                text-align: center;
                justify-content: center;
            }
        }
    }

</style>
<style lang="scss">
.crud-table{
    .e-data-table{
        tr{
            white-space: nowrap;
            td{
                max-width: 300px;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }
        .v-data-table__wrapper > table{
            thead.v-data-table-header{
                background-color: #fafafa;
                tr>th{
                    height: 40px!important;
                    border-top: thin solid rgba(0, 0, 0, 0.1) !important;
                }
            }
        }
    }
    .theme--light.v-btn--active::before{
        opacity: 0.04;
    }
    .v-text-field--full-width .v-input__prepend-outer, .v-text-field--full-width .v-input__prepend-inner, .v-text-field--full-width .v-input__append-inner, .v-text-field--full-width .v-input__append-outer, .v-text-field--enclosed .v-input__prepend-outer, .v-text-field--enclosed .v-input__prepend-inner, .v-text-field--enclosed .v-input__append-inner, .v-text-field--enclosed .v-input__append-outer{
        margin-top: 9px!important;
    }
    .v-text-field--outlined.v-input--dense .v-label{
        top: 13px;
    }
}
</style>
